import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { AuditSettings } from '../types';

interface AuditSectionProps {
  settings: AuditSettings;
  onUpdate: (settings: AuditSettings) => void;
  isAdmin: boolean;
}

const AuditSection = ({ settings, onUpdate, isAdmin }: AuditSectionProps) => {
  const [formData, setFormData] = useState(settings);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAllRecords, setShowAllRecords] = useState(false);
  const [allActivities, setAllActivities] = useState([
    {
      user: 'Ana Silva',
      action: 'Criou nova consulta tributária',
      timestamp: new Date(Date.now() - 300000),
      type: 'create'
    },
    {
      user: 'Carlos Santos',
      action: 'Exportou relatório de consultas',
      timestamp: new Date(Date.now() - 900000),
      type: 'export'
    },
    {
      user: 'Maria Oliveira',
      action: 'Atualizou configurações de API',
      timestamp: new Date(Date.now() - 1800000),
      type: 'update'
    },
    {
      user: 'João Pereira',
      action: 'Visualizou histórico de consultas',
      timestamp: new Date(Date.now() - 3600000),
      type: 'view'
    },
    {
      user: 'Sistema',
      action: 'Renovação automática de token realizada',
      timestamp: new Date(Date.now() - 7200000),
      type: 'system'
    },
    {
      user: 'Ana Silva',
      action: 'Adicionou novo membro à equipe',
      timestamp: new Date(Date.now() - 10800000),
      type: 'create'
    },
    {
      user: 'Roberto Lima',
      action: 'Modificou permissões de usuário',
      timestamp: new Date(Date.now() - 14400000),
      type: 'update'
    },
    {
      user: 'Fernanda Costa',
      action: 'Exportou dados para CSV',
      timestamp: new Date(Date.now() - 18000000),
      type: 'export'
    },
    {
      user: 'Paulo Mendes',
      action: 'Acessou relatório de auditoria',
      timestamp: new Date(Date.now() - 21600000),
      type: 'view'
    },
    {
      user: 'Sistema',
      action: 'Backup automático realizado',
      timestamp: new Date(Date.now() - 25200000),
      type: 'system'
    }
  ]);

  const logLevelOptions = [
    { value: 'minimal', label: 'Mínimo' },
    { value: 'standard', label: 'Padrão' },
    { value: 'detailed', label: 'Detalhado' }
  ];

  const handleChange = (field: keyof AuditSettings, value: any) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(formData);
    setHasChanges(false);
  };

  const handleViewAllRecords = () => {
    setShowAllRecords(true);
  };

  const handleExportAuditLog = () => {
    const csvContent = [
      ['Usuário', 'Ação', 'Data/Hora', 'Tipo'].join(','),
      ...allActivities.map(a => [
        a.user,
        a.action,
        a.timestamp.toLocaleString('pt-BR'),
        a.type
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `auditoria_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    alert('✅ Log de auditoria exportado com sucesso!');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Configurações de Auditoria</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie políticas de registro e rastreamento de atividades
          </p>
        </div>
        {hasChanges && isAdmin && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setFormData(settings)}>
              Descartar
            </Button>
            <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSave}>
              Salvar Configurações
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
          Políticas de Registro
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Retenção de Logs (dias)"
            type="number"
            value={formData.retentionDays}
            onChange={(e) => handleChange('retentionDays', Number(e.target.value))}
            disabled={!isAdmin}
            min={30}
            max={3650}
            description="Período de armazenamento dos registros"
          />

          <Select
            label="Nível de Detalhamento"
            description="Quantidade de informações registradas"
            options={logLevelOptions}
            value={formData.logLevel}
            onChange={(value) => handleChange('logLevel', value)}
            disabled={!isAdmin}
          />

          <div className="flex items-center">
            <Checkbox
              label="Rastrear Atividade de Usuários"
              description="Registrar ações dos usuários no sistema"
              checked={formData.trackUserActivity}
              onChange={(e) => handleChange('trackUserActivity', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>

          <div className="flex items-center">
            <Checkbox
              label="Rastrear Requisições de API"
              description="Registrar todas as chamadas à API externa"
              checked={formData.trackAPIRequests}
              onChange={(e) => handleChange('trackAPIRequests', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>

          <div className="flex items-center">
            <Checkbox
              label="Rastrear Alterações de Dados"
              description="Registrar modificações em registros"
              checked={formData.trackDataChanges}
              onChange={(e) => handleChange('trackDataChanges', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>

          <div className="flex items-center">
            <Checkbox
              label="Permitir Exportação de Logs"
              description="Habilitar download de registros de auditoria"
              checked={formData.exportAuditLogs}
              onChange={(e) => handleChange('exportAuditLogs', e.target.checked)}
              disabled={!isAdmin}
            />
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Activity" size={20} color="var(--color-primary)" />
          Atividades Recentes
        </h3>
        <div className="space-y-3">
          {[
            {
              user: 'Ana Silva',
              action: 'Criou nova consulta tributária',
              timestamp: new Date(Date.now() - 300000),
              type: 'create'
            },
            {
              user: 'Carlos Santos',
              action: 'Exportou relatório de consultas',
              timestamp: new Date(Date.now() - 900000),
              type: 'export'
            },
            {
              user: 'Maria Oliveira',
              action: 'Atualizou configurações de API',
              timestamp: new Date(Date.now() - 1800000),
              type: 'update'
            },
            {
              user: 'João Pereira',
              action: 'Visualizou histórico de consultas',
              timestamp: new Date(Date.now() - 3600000),
              type: 'view'
            },
            {
              user: 'Sistema',
              action: 'Renovação automática de token realizada',
              timestamp: new Date(Date.now() - 7200000),
              type: 'system'
            }
          ].map((activity, index) => (
            <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-md">
              <Icon
                name={
                  activity.type === 'create' ?'FilePlus'
                    : activity.type === 'export' ?'Download'
                    : activity.type === 'update' ?'Edit'
                    : activity.type === 'view' ?'Eye' :'Settings'
                }
                size={16}
                color="var(--color-muted-foreground)"
                className="mt-0.5"
              />
              <div className="flex-1">
                <p className="text-sm text-foreground">
                  <span className="font-medium">{activity.user}</span> {activity.action}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {activity.timestamp.toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
          ))}
        </div>
        <Button variant="outline" iconName="Eye" iconPosition="left" fullWidth className="mt-4" onClick={handleViewAllRecords}>
          Ver Todos os Registros
        </Button>
      </div>

      {showAllRecords && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAllRecords(false)}
          />
          <div className="relative bg-card rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Todos os Registros de Auditoria</h3>
                <p className="text-sm text-muted-foreground">{allActivities.length} registros encontrados</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={handleExportAuditLog}>
                  Exportar
                </Button>
                <button
                  onClick={() => setShowAllRecords(false)}
                  className="p-1 hover:bg-muted rounded"
                >
                  <Icon name="X" size={20} color="var(--color-muted-foreground)" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-2">
                {allActivities.map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-muted rounded-md hover:bg-muted/80 transition-colors">
                    <Icon
                      name={
                        activity.type === 'create' ?'FilePlus'
                          : activity.type === 'export' ?'Download'
                          : activity.type === 'update' ?'Edit'
                          : activity.type === 'view' ?'Eye' :'Settings'
                      }
                      size={16}
                      color="var(--color-muted-foreground)"
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">{activity.user}</span> {activity.action}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {activity.timestamp.toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      activity.type === 'create' ? 'bg-success/10 text-success' :
                      activity.type === 'export' ? 'bg-primary/10 text-primary' :
                      activity.type === 'update' ? 'bg-warning/10 text-warning' :
                      activity.type === 'view'? 'bg-info/10 text-info' : 'bg-muted text-muted-foreground'
                    }`}>
                      {activity.type}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" size={20} color="var(--color-primary)" />
          Estatísticas de Auditoria
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Users" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-muted-foreground">Usuários Ativos</span>
            </div>
            <p className="text-2xl font-bold text-foreground">24</p>
          </div>

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Activity" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-muted-foreground">Ações Hoje</span>
            </div>
            <p className="text-2xl font-bold text-foreground">1,847</p>
          </div>

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Database" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-muted-foreground">Logs Armazenados</span>
            </div>
            <p className="text-2xl font-bold text-foreground">45.2K</p>
          </div>

          <div className="p-4 bg-muted rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-muted-foreground">Alertas</span>
            </div>
            <p className="text-2xl font-bold text-foreground">3</p>
          </div>
        </div>
      </div>

      {!isAdmin && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">Acesso Restrito</p>
              <p className="text-sm text-muted-foreground mt-1">
                Apenas administradores podem modificar as configurações de auditoria. Entre em
                contato com seu supervisor para alterações.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditSection;